import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const titleHeadVariants = cva('font-bold tracking-tight', {
	variants: {
		variant: {
			default: 'text-gray-900 dark:text-gray-100',
			primary: 'text-blue-600 dark:text-blue-400',
			secondary: 'text-gray-700 dark:text-gray-300',
			destructive: 'text-red-600 dark:text-red-400',
			success: 'text-green-600 dark:text-green-400',
		},
		size: {
			h1: 'text-4xl md:text-5xl',
			h2: 'text-3xl md:text-4xl',
			h3: 'text-2xl md:text-3xl',
			h4: 'text-xl md:text-2xl',
			h5: 'text-lg md:text-xl',
			h6: 'text-base md:text-lg',
		},
	},
	defaultVariants: {
		variant: 'default',
		size: 'h2',
	},
});

// Definir los tamaños como tipos válidos
type TitleHeadTag = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

export interface TitleHeadProps
	extends React.HTMLAttributes<HTMLHeadingElement>,
		VariantProps<typeof titleHeadVariants> {
	asChild?: boolean;
	size: TitleHeadTag;
}

export const TitleHead = React.forwardRef<HTMLHeadingElement, TitleHeadProps>(
	({ className, variant, size, asChild = false, ...props }, ref) => {
		// Se fuerza TypeScript a reconocer los valores como etiquetas de encabezado válidas
		const Comp = asChild ? Slot : (size as TitleHeadTag);

		return (
			<Comp
				className={cn(titleHeadVariants({ variant, size, className }))}
				ref={ref}
				{...props}
			/>
		);
	},
);

TitleHead.displayName = 'TitleHead';
